import React, { useEffect } from "react"; // Add useEffect
import { useLocation } from "react-router-dom"; // Add useLocation
import Navbar from "./navbar";
import Footer from "./footer";
import FeatureSection from "./featuressection";
import { ArrowRight } from "lucide-react";

const Features = () => {
  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      const elementId = location.hash.slice(1); // Remove '#'
      const element = document.getElementById(elementId);
      if (element) {
        element.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    } else {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [location]);

  return (
    <>
      <Navbar />

      <div className="features-page" style={{ paddingTop: "80px" }}>
        {/* Hero Section */}
        <section className="features-hero  pb-5">
          <div className="container">
            <div className="hero-content">
              <div className="hero-badge">
                {/* <span className="badge-text">All-in-One Platform</span> */}
              </div>
              <h1 className="hero-titlle">
                Everything You Need to
                <span className="gradient-text"> Run Your Business</span>
              </h1>
              <p className="hero-subtitlle">
                Manage sales, inventory, expenses, employees, and reports in one
                integrated platform. Trusted by 500+ businesses across India.
              </p>
              <div className="hero-cta">
                <a href="/login">
                  {" "}
                  <button className="cta-btn cta-btn-primary">
                    Start Free Trial
                    <ArrowRight size={18} className="ms-2" />
                  </button>
                </a>

                {/* <button className="cta-btn cta-btn-outline">
                  Watch Demo Video
                </button> */}
              </div>
            </div>
          </div>
        </section>

        {/* Feature Sections */}
        <FeatureSection
          id="inventory"
          badge="Items & Products"
          title="Inventory Management"
          subtitle="Manage your inventory efficiently with real-time tracking, multi-location support, and automated stock updates."
          points={[
            "Multi-location inventory tracking",
            "Batch & expiry date management",
            "Search, filter, and quick access to products",
            "Add, update, or delete items, categories, and units",
            "Low stock alerts & inventory valuation reports",
            "Barcode scanning support",
          ]}
          image="/assets/img/features-img/img-1.png"
        />

        <FeatureSection
          id="services"
          badge="Services"
          title="Services Management"
          subtitle="Easily manage all your services, categorize them, track pricing & GST, and streamline order and payment management."
          points={[
            "Easily add & update services",
            "Quickly organize services by categories",
            "Track GST & SAC codes effortlessly",
            "Instant search & filter access",
            "Monitor service history & transactions",
            "Seamless order & payment management",
          ]}
          image="/assets/img/features-img/img-2.png"
          reverse
        />

        <FeatureSection
          id="parties"
          badge="Parties"
          title="Customer & Vendor Management"
          subtitle="Get a clear view of your business finances with automated tracking, balance management, and detailed party insights."
          points={[
            "Effortlessly manage customers & suppliers",
            "Instantly track balances & outstanding amounts",
            "Organize parties by category",
            "Access transaction history anytime",
            "Quickly add, update, or remove parties",
            "Seamless integration with accounting reports",
          ]}
          image="/assets/img/features-img/img-3.png"
        />

        <FeatureSection
          id="sales"
          badge="Sales"
          title="Sales & Order Management"
          subtitle="Manage your complete sales lifecycle — from sale orders to billing, returns, payments, and customer ledgers."
          points={[
            "Create and manage sales invoices",
            "View and track all sales centrally",
            "Handle sale returns with validation",
            "Track due amounts & payments",
            "Instant invoice printing & sharing",
            "Convert orders into final sales",
            "Customer ledger view",
          ]}
          image="/assets/img/features-img/img-4.png"
          reverse
        />

        <FeatureSection
          id="purchase"
          badge="Purchases"
          title="Purchase & Order Management"
          subtitle="Manage your procurement lifecycle — from purchase orders to final purchases — with GST-ready billing and vendor tracking."
          points={[
            "Create & manage purchase orders",
            "Convert orders to purchases",
            "Update GST-ready invoices",
            "Auto-fill prices from history",
            "Inclusive & exclusive GST calculations",
            "Track paid & due amounts",
            "Handle purchase returns",
            "Vendor ledger & balance view",
            "Other expense management",
            "Auto inventory updates on purchase",
          ]}
          image="/assets/img/features-img/img-5.png"
        />

        <FeatureSection
          id="expenses"
          badge="Expenses"
          title="Expense Management"
          subtitle="Track, categorize, and control your business expenses with complete visibility and reporting."
          points={[
            "Add, edit, manage expenses",
            "Category, date, and payment tracking",
            "Support paid, pending, cancelled statuses",
            "Date range filtering",
            "Automatic ledger entries",
            "View expense impact in ledger",
            "Manage dedicated expense categories",
            "Prevent deletion of linked categories",
          ]}
          image="/assets/img/features-img/img-6.png"
          reverse
        />

        <FeatureSection
          id="returns"
          badge="Return"
          title="Purchase & Sale Returns"
          subtitle="Track and manage all purchase and sale returns in one place with detailed visibility."
          points={[
            "Manage purchase & sale returns",
            "View returned items with date",
            "Identify return type automatically",
            "Link returns to original records",
            "Vendor & customer name resolution",
            "Track return reasons & status",
            "Paginated listing for large datasets",
            "Party & date-based filtering",
            "Inventory adjustment support",
          ]}
          image="/assets/img/features-img/img-7.png"
        />

        <FeatureSection
          id="employee"
          badge="Employee"
          title="Employee & Attendance"
          subtitle="Manage employees, work shifts, and attendance with flexible shift rules and clear visibility."
          points={[
            "Manage employee profiles & roles",
            "Add, edit, remove employees securely",
            "Define work shifts & grace rules",
            "Configure half-day & overtime",
            "Punch-in/punch-out tracking",
            "Daily & monthly attendance view",
            "Track attendance status",
            "Attendance performance indicators",
            "Mobile-friendly monitoring",
          ]}
          image="/assets/img/features-img/img-10.png"
          reverse
        />

        <FeatureSection
          id="transactions"
          badge="Transaction"
          title="Transaction & Ledger"
          subtitle="View and track all financial transactions across sales, purchases, and payments with party-wise ledger visibility."
          points={[
            "Centralized view of transactions",
            "Party-wise ledger tracking",
            "Support sales, purchases, orders, payments",
            "Reference-type based classification",
            "Search transactions by party",
            "Date range filtering",
            "View total & due amounts",
            "Click for detailed ledger entry",
            "Paginated, optimized listings",
          ]}
          image="/assets/img/features-img/img-8.png"
        />

        <FeatureSection
          id="reports"
          badge="Reports"
          title="Business Reports & Exports"
          subtitle="Generate detailed business reports with flexible filters and export them instantly for accounting, auditing, and analysis."
          points={[
            "Sales & purchase reports with filters",
            "Ledger & transaction reports",
            "Expense reports by category",
            "Item-wise & stock reports",
            "Customer & supplier reports",
            "Advanced filter customization",
            "Instant CSV export",
            "Download history & re-download support",
          ]}
          image="/assets/img/features-img/img-9.png"
          reverse
        />

        {/* CTA Section */}
        <section
          className="features-cta py-5"
          style={{
            background: "linear-gradient(135deg, #1d1971, #3B82F6)",
            color: "#fff",
          }}
        >
          <div className="container text-center">
            <h2
              className="cta-title mb-3"
              style={{ fontSize: "2rem", fontWeight: "700" }}
            >
              Ready to Transform Your Business?
            </h2>
            <p
              className="cta-subtitle mb-4"
              style={{ fontSize: "1.1rem", opacity: 0.9 }}
            >
              Join 50,000+ businesses using our platform to save time, reduce
              errors, and grow faster.
            </p>

            <div className="cta-buttons d-flex justify-content-center gap-3 flex-wrap mb-4">
              <a href="/login">
                <button
                  className="cta-btn cta-btn-primary"
                  style={{
                    padding: "0.75rem 1.5rem",
                    borderRadius: "50px",
                    border: "none",
                    background: "#fff",
                    color: "#4F46E5",
                    fontWeight: "600",
                    cursor: "pointer",
                    transition: "all 0.3s",
                  }}
                >
                  Get Started Free for 14 Days
                </button>
              </a>

              <a href="/login">
                {" "}
                <button
                  className="cta-btn cta-btn-outline"
                  style={{
                    padding: "0.75rem 1.5rem",
                    borderRadius: "50px",
                    border: "2px solid #fff",
                    background: "transparent",
                    color: "#fff",
                    fontWeight: "600",
                    cursor: "pointer",
                    transition: "all 0.3s",
                  }}
                >
                  Schedule a Demo
                </button>
              </a>
            </div>

            <div
              className="trust-badges d-flex justify-content-center gap-4 flex-wrap"
              style={{ fontSize: "0.9rem", opacity: 0.85 }}
            >
              <span className="trust-badge">✓ No credit card required</span>
              <span className="trust-badge">✓ Free onboarding support</span>
              <span className="trust-badge">✓ Cancel anytime</span>
            </div>
          </div>
        </section>
      </div>

      <Footer />
    </>
  );
};

export default Features;
